import {
    useCallback,
    useMemo, useState,
} from 'react'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'

import useHasPermission from 'app/hooks/useHasPermission'
import RichTable from 'app/shared-components/RichTableReduxWrapper'
import Card from 'app/shared-components/Card'
import useLabel from 'app/hooks/useLabel'
import SharedContextProvider from 'app/shared-components/SharedContext'
import StatusHandler from 'app/shared-components/StatusHandler'

import useGetLoggerByLoggerNumber from '../hooks/useGetLoggerByLoggerNumber'

import useStyles from './Calibrations.style'
import columns from './calibrations.columns'
import useGetLoggerDocuments from './hooks/useGetLoggerDocuments'
import useAddLoggerDocument from './hooks/useAddLoggerDocument'
import UploadCalibrationDocumentDialog from './UploadCalibrationDocumentDialog'

const Calibrations = ({
    id: loggerNumber,
}: { id: string }) => {
    const {
        classes,
    } = useStyles()
    const canLoggerUpdate = useHasPermission(['logger_update'])
    const canLoggerDelete = useHasPermission(['logger_delete'])
    const l = useLabel()
    const [
        openConfirmDialog,
        setOpenConfirmDialog,
    ] = useState(false)

    const {
        data,
        isDataReady,
        isFetching,
        isError,
        error,
    } = useGetLoggerByLoggerNumber(loggerNumber)

    const fields = useMemo(() => {
        return columns({
            l,
            canLoggerDelete,
        })
    }, [
        l,
        canLoggerDelete,
    ])

    const {
        onParamsChange,
        load,
        refresh,
    } = useGetLoggerDocuments(data?.id)

    const {
        mutate,
    } = useAddLoggerDocument(refresh)

    const onSuccessUpload = (idDocument: number, calibratedOn: string) => {
        mutate({
            loggerId: data?.id,
            data: {
                document: {
                    id: idDocument,
                },
                calibratedOn,
            },
        })
        setOpenConfirmDialog(false)
    }
    const askConfirmation = useCallback(() => {
        setOpenConfirmDialog(true)
    }, [])

    const onCancelUpload = useCallback(() => {
        setOpenConfirmDialog(false)
    }, [])

    return (
        <Card contentClass={classes.cardContent}>
            <StatusHandler
                isSuccess={isDataReady}
                isFetching={isFetching}
                isError={isError}
                error={error}
            >
                <>
                    <div className={classes.header}>
                        <Typography variant="h3">
                            Documents
                        </Typography>
                        <div className={classes.iconWrapper}>
                            {canLoggerUpdate && (
                                <IconButton
                                    onClick={askConfirmation}
                                >
                                    <img
                                        src="assets/images/icons/upload_file.svg"
                                        alt="upload"
                                    />
                                </IconButton>
                            )}
                        </div>
                    </div>
                    <UploadCalibrationDocumentDialog
                        onSuccess={onSuccessUpload}
                        open={openConfirmDialog}
                        onCancel={onCancelUpload}
                    />
                    <SharedContextProvider
                        value={{
                            loggerId: data?.id,
                            updateLoggerDocuments: refresh,
                        }}
                    >
                        <RichTable
                            configName="calibrationDocument"
                            onParamsChange={onParamsChange}
                            name="calibrationDocument"
                            load={load}
                            columns={fields}
                            uniqField="id"
                            className={classes.tableContent}
                            classNames={{
                                contentWrapper: classes.tableContentWrapper,
                            }}
                        />
                    </SharedContextProvider>
                </>
            </StatusHandler>
        </Card>
    )
}

export default Calibrations
